import React from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTemplates } from "../hooks/custom/useTemplates";
import TemplatesHeader from "../components/ui/TemplatesHeader";
import TemplatesGrid from "../components/tables/TemplatesGrid";
import CreateTemplateModal from "../components/modals/CreateTemplateModal";
import PreviewTemplateModal from "../components/modals/PreviewTemplateModal";
import DeleteTemplateModal from "../components/modals/DeleteTemplateModal";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Import existing modals
import PreBuiltTemplatesModal from "../../src/components/modals/PreBuiltTemplatesModal";

const TemplatesPage: React.FC = () => {
  usePageTitle("Email Templates");

  const {
    // Data
    templates,
    templatesLoading,

    // Search
    searchTerm,
    setSearchTerm,

    // Modal states
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    showDeleteModal,
    showPreBuiltModal,
    setShowPreBuiltModal,
    showPreviewModal,
    previewTemplate,
    template,
    setTemplate,
    deletingTemplate,
    showPreview,
    setShowPreview,

    // Form states
    isLoading,

    // Handlers
    handleCreateTemplate,
    handleEditTemplate,
    handleUpdateTemplate,
    handleDeleteTemplate,
    handleConfirmDelete,
    handlePreviewTemplate,
    handleDuplicateTemplate,
    handleRefresh,
    handleCloseCreateModal,
    handleCloseEditModal,
    handleCloseDeleteModal,
    handleClosePreviewModal,
  } = useTemplates();

  return (
    <div className="p-6">
      {/* Back Button */}
      <div className="mb-4">
        <Link
          to="/bulk-email"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Bulk Email
        </Link>
      </div>

      {/* Header */}
      <TemplatesHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onCreateTemplate={() => setShowCreateModal(true)}
        onShowPreBuilt={() => setShowPreBuiltModal(true)}
        onRefresh={handleRefresh}
        templatesCount={templates.length}
        isLoading={templatesLoading}
      />

      {/* Templates Grid */}
      <TemplatesGrid
        templates={templates}
        searchTerm={searchTerm}
        onEditTemplate={handleEditTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        onPreviewTemplate={handlePreviewTemplate}
        onDuplicateTemplate={handleDuplicateTemplate}
        isLoading={templatesLoading}
      />

      {/* Modals */}
      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        template={template}
        setTemplate={setTemplate}
        onSave={handleCreateTemplate}
        isLoading={isLoading}
        showPreview={showPreview}
        setShowPreview={setShowPreview}
      />

      <CreateTemplateModal
        mode="edit"
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        template={template}
        setTemplate={setTemplate}
        onSave={handleUpdateTemplate}
        isLoading={isLoading}
        showPreview={showPreview}
        setShowPreview={setShowPreview}
      />

      {/* Pre-built Templates Modal */}
      <PreBuiltTemplatesModal
        isOpen={showPreBuiltModal}
        onClose={() => setShowPreBuiltModal(false)}
        onSelectTemplate={(templateData) => {
          setTemplate({
            name: templateData.name,
            subject: templateData.subject,
            content: templateData.content,
          });
          setShowPreBuiltModal(false);
          setShowCreateModal(true);
        }}
      />

      <PreviewTemplateModal
        isOpen={showPreviewModal}
        onClose={handleClosePreviewModal}
        template={previewTemplate}
      />

      <DeleteTemplateModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        template={deletingTemplate}
        onConfirmDelete={handleConfirmDelete}
        isLoading={isLoading}
      />
    </div>
  );
};

export default TemplatesPage;
